/**
 * Data Migration Service
 * Handles transferring anonymous user data to authenticated accounts
 */

import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  writeBatch,
  WriteBatch,
  getDoc,
  setDoc,
  DocumentData,
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from '@/lib/firebase';
import { getAnonymousUserProfile } from './profileService';
import { createActivity } from './activityService';

export interface DataMigrationResult {
  success: boolean;
  pollsMigrated: number;
  votesMigrated: number;
  activitiesMigrated: number;
  error?: string;
}

/**
 * Migrates all anonymous user data to a new authenticated account
 */
export async function migrateAnonymousUserData(
  anonymousUserId: string,
  newUser: User
): Promise<DataMigrationResult> {
  const result: DataMigrationResult = {
    success: false,
    pollsMigrated: 0,
    votesMigrated: 0,
    activitiesMigrated: 0,
  };

  try {
    console.log('Starting data migration from', anonymousUserId, 'to', newUser.uid);

    // Create a batch for atomic operations
    const batch = writeBatch(db);

    // 1. Migrate user profile data
    await migrateUserProfile(anonymousUserId, newUser);

    // 2. Migrate polls created by anonymous user
    result.pollsMigrated = await migratePollsOwnership(anonymousUserId, newUser.uid, batch);

    // 3. Migrate vote records
    result.votesMigrated = await migrateVoteRecords(anonymousUserId, newUser.uid, batch);

    // 4. Migrate activity logs
    result.activitiesMigrated = await migrateActivityLogs(anonymousUserId, newUser.uid, batch);

    // Commit all changes atomically
    await batch.commit();

    // 5. Clean up anonymous user data from localStorage
    cleanupAnonymousUserData(anonymousUserId);

    // 6. Log the migration activity
    await createActivity({
      type: 'profile_updated',
      userId: newUser.uid,
      userName: newUser.displayName || newUser.email || 'User',
      userPhotoURL: newUser.photoURL || undefined,
      pollId: '',
      pollTitle: 'Account Migration',
      isAnonymous: false,
    });

    result.success = true;
    return result;
  } catch (error) {
    console.error('Error during data migration:', error);
    result.error = error instanceof Error ? error.message : 'Unknown error occurred';
    return result;
  }
}

/**
 * Migrates user profile data from anonymous to authenticated account
 */
async function migrateUserProfile(anonymousUserId: string, newUser: User): Promise<void> {
  try {
    // Get anonymous user profile from localStorage
    const anonymousProfile = getAnonymousUserProfile(anonymousUserId);

    if (!anonymousProfile) {
      console.log('No anonymous profile found to migrate');
      return;
    }

    console.log('Migrating anonymous profile:', anonymousProfile);

    // Create or update the authenticated user's profile in Firestore
    const userDocRef = doc(db, 'users', newUser.uid);
    const userDoc = await getDoc(userDocRef);

    // Determine auth method
    let authMethod: 'email' | 'google' = 'email';
    if (newUser.providerData.length > 0) {
      const provider = newUser.providerData[0].providerId;
      if (provider === 'google.com') {
        authMethod = 'google';
      }
    }

    // Filter out undefined values to avoid Firestore errors
    const profileData: Record<string, unknown> = {
      displayName: anonymousProfile.displayName || newUser.displayName || '',
      email: newUser.email || '',
      authMethod,
      updatedAt: new Date(),
    };

    // Avatar migration logic - preserve anonymous user's avatar choice
    if (anonymousProfile.avatarId !== undefined) {
      // Anonymous user had a random avatar - preserve it
      profileData.avatarId = anonymousProfile.avatarId;
      profileData.useGoogleAvatar = false; // Explicitly set to false to preserve choice
      profileData.customAvatarUrl = null; // Clear any conflicting fields
    } else if (anonymousProfile.customAvatarUrl !== undefined) {
      // Anonymous user had a custom uploaded avatar - preserve it
      profileData.customAvatarUrl = anonymousProfile.customAvatarUrl;
      profileData.useGoogleAvatar = false; // Explicitly set to false to preserve choice
      profileData.avatarId = null; // Clear any conflicting fields
    } else if (anonymousProfile.useGoogleAvatar !== undefined) {
      // Anonymous user had explicitly set Google avatar preference - preserve it
      profileData.useGoogleAvatar = anonymousProfile.useGoogleAvatar;
      if (anonymousProfile.useGoogleAvatar) {
        profileData.avatarId = null;
        profileData.customAvatarUrl = null;
      }
    } else {
      // No avatar preference from anonymous user - set defaults based on auth method
      if (authMethod === 'google') {
        profileData.useGoogleAvatar = true;
        profileData.avatarId = null;
        profileData.customAvatarUrl = null;
      } else {
        // For email accounts, don't set any avatar preference - let the system generate one
        profileData.useGoogleAvatar = false;
      }
    }

    console.log('Final profile data to be saved:', profileData);

    if (userDoc.exists()) {
      // Update existing document, preserving any existing data
      await updateDoc(userDocRef, profileData as DocumentData);
    } else {
      // Create new document
      await setDoc(userDocRef, profileData);
    }

    console.log('User profile migrated successfully');
  } catch (error) {
    console.error('Error migrating user profile:', error);
    throw error;
  }
}

/**
 * Migrates poll ownership from anonymous user to authenticated user
 */
async function migratePollsOwnership(
  anonymousUserId: string,
  newUserId: string,
  batch: WriteBatch
): Promise<number> {
  try {
    // Find all polls created by the anonymous user
    const pollsQuery = query(
      collection(db, 'polls'),
      where('createdBy', '==', anonymousUserId)
    );

    const pollsSnapshot = await getDocs(pollsQuery);
    let migratedCount = 0;

    for (const pollDoc of pollsSnapshot.docs) {
      const pollRef = doc(db, 'polls', pollDoc.id);
      const pollData = pollDoc.data();

      // Update poll ownership
      batch.update(pollRef, {
        createdBy: newUserId,
        // Keep the original createdByName if it exists, or use the new user's name
        createdByName: pollData.createdByName || 'User',
      });

      migratedCount++;
    }

    console.log(`Migrated ${migratedCount} polls`);
    return migratedCount;
  } catch (error) {
    console.error('Error migrating polls:', error);
    throw error;
  }
}

/**
 * Migrates vote records from anonymous user to authenticated user
 */
async function migrateVoteRecords(
  anonymousUserId: string,
  newUserId: string,
  batch: WriteBatch
): Promise<number> {
  try {
    // Find all polls to check for votes by the anonymous user
    const pollsSnapshot = await getDocs(collection(db, 'polls'));
    let migratedCount = 0;

    // Fetch vote snapshots for every poll concurrently; independent reads.
    const voteSnapshots = await Promise.all(
      pollsSnapshot.docs.map(pollDoc =>
        getDocs(query(
          collection(db, 'polls', pollDoc.id, 'votes'),
          where('userId', '==', anonymousUserId)
        )).then(snap => ({ pollId: pollDoc.id, snap }))
      )
    );

    for (const { pollId, snap } of voteSnapshots) {
      for (const voteDoc of snap.docs) {
        const voteRef = doc(db, 'polls', pollId, 'votes', voteDoc.id);
        const voteData = voteDoc.data();

        batch.update(voteRef, {
          userId: newUserId,
          userName: voteData.userName || 'User',
          isAnonymous: false,
        });

        migratedCount++;
      }
    }

    console.log(`Migrated ${migratedCount} vote records`);
    return migratedCount;
  } catch (error) {
    console.error('Error migrating vote records:', error);
    throw error;
  }
}

/**
 * Migrates activity logs from anonymous user to authenticated user
 */
async function migrateActivityLogs(
  anonymousUserId: string,
  newUserId: string,
  batch: WriteBatch
): Promise<number> {
  try {
    // Find all activities by the anonymous user
    const activitiesQuery = query(
      collection(db, 'activities'),
      where('userId', '==', anonymousUserId)
    );

    const activitiesSnapshot = await getDocs(activitiesQuery);
    let migratedCount = 0;

    for (const activityDoc of activitiesSnapshot.docs) {
      const activityRef = doc(db, 'activities', activityDoc.id);
      const activityData = activityDoc.data();

      // Update activity record with new user ID
      batch.update(activityRef, {
        userId: newUserId,
        // Keep the original userName if it exists
        userName: activityData.userName || 'User',
        isAnonymous: false,
      });

      migratedCount++;
    }

    console.log(`Migrated ${migratedCount} activity records`);
    return migratedCount;
  } catch (error) {
    console.error('Error migrating activity logs:', error);
    throw error;
  }
}

/**
 * Cleans up anonymous user data from localStorage
 */
function cleanupAnonymousUserData(anonymousUserId: string): void {
  try {
    // Remove user-specific anonymous profile
    localStorage.removeItem(`anonymousProfile_${anonymousUserId}`);

    // Also remove global anonymous profile if it exists (for backward compatibility)
    localStorage.removeItem('anonymousProfile');


  } catch (error) {
    console.error('Error cleaning up anonymous user data:', error);
    // Don't throw error for cleanup failure
  }
}

